import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Drawer } from "component/ui/Drawer";
import { toast } from "react-toastify";
import { Button } from "component/ui/Button";
import { MAX_IMAGE_FILE_SIZE } from "constants/global";
import FirstTimeRequest from "./FirstTimeRequest";
import SubsequentRequest from "./SubsequentRequest";
import { useQuery } from "react-query";
import { fetchBusinessDocuments, fetchCreditGroups } from "api/credit-fetchers";
import useUserDetails from "hooks/useUserDetails";
import Loading from "component/loading";
import { checkExpiry } from "component/flex/utils";
import { DrawerContent, DrawerTrigger } from "component/ui/Drawer";
import { sampleDocuments } from "./FirstTimeRequest";
import { cn } from "lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "component/ui/Tooltip";

const validateFile = (file) => {
    if (!file) return false;
    if (file.size > MAX_IMAGE_FILE_SIZE) {
        toast.error(`File size exceeds the limit of 5MB.`);
        return false;
    }
    return true;
};

const GenericBtn = ({ disabled, text, onClick, className, hasHover, ...props }) => {
    return (
        <Tooltip>
            <TooltipTrigger>
                <Button
                    disabled={disabled}
                    onClick={() => {
                        if (disabled) return;
                        onClick?.();
                    }}
                    className={cn(
                        `hover:opacity-90 text-left mt-0 sm:!mt-0 flex items-center gap-2 rounded-lg py-2 h-[48px] px-3 text-xs md:text-sm`,
                        disabled ? "bg-[#EBE4FF] text-[#BFA6FF]" : "bg-[#6F00FF] text-[#ffff]",
                        className
                    )}
                    {...props}
                >
                    {text}
                </Button>
            </TooltipTrigger>
            {hasHover && <TooltipContent>{hasHover}</TooltipContent>}
        </Tooltip>
    );
};

const FinanceRequest = () => {
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [isFirstTime, setIsFirstTime] = useState(true);
    const [bankFile, setBankFile] = useState(null);
    const [boardFile, setBoardFile] = useState(null);
    const [fileKey, setFileKey] = useState("board_resolution");
    const { businessId, currency, isLoadingCurrentBusinessAccount, currentBusinessAccount } = useUserDetails();

    const { data, isLoading: isLoadingBusinessDocsList } = useQuery({
        queryKey: ["checkBusinessDocuments"],
        queryFn: () => fetchBusinessDocuments({ businessId }),
    });
    const { data: groupData, isLoading: isLoadingFinanceGroup } = useQuery({
        queryKey: ["fetchCreditGroups"],
        queryFn: () =>
            fetchCreditGroups({
                businessId,
                currency,
            }),
    });
    const financeGroupList = groupData?.data ?? {};
    const isValid = data?.data?.every((item) => checkExpiry(item.expiryDate) !== "Expired");
    const canProceed = bankFile && boardFile;
    useEffect(() => {
        if (isValid && data?.data?.length === 2) {
            setIsFirstTime(false);
        } else {
            setIsFirstTime(true);
        }
    }, [data]);

    return (
        <Drawer direction="right">
            {(isLoadingBusinessDocsList ||
                isLoadingFinanceGroup ||
                isLoadingCurrentBusinessAccount) && (
                <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
                    <Loading />
                </div>
            )}
            <main className="w-full overflow-hidden bg-[#FFFFFF]">
                <div className="max-w-[2000px] mx-auto w-screen h-screen bg-[#FFFFFF]">
                    {isFirstTime ? (
                        <FirstTimeRequest
                            bankFile={bankFile}
                            setBankFile={setBankFile}
                            boardFile={boardFile}
                            setBoardFile={setBoardFile}
                            setIsFirstTime={setIsFirstTime}
                            canProceed={!canProceed}
                            fileKey={fileKey}
                            setFileKey={setFileKey}
                            isValid={isValid}
                        />
                    ) : (
                        <SubsequentRequest
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            setIsFirstTime={setIsFirstTime}
                            isFirstTime={isFirstTime}
                            canProceed={!canProceed}
                            fileKey={fileKey}
                            setFileKey={setFileKey}
                            groupList={financeGroupList}
                            currentBusinessAccount={currentBusinessAccount}
                        />
                    )}
                </div>
                <DrawerContent className="z-[999] data-[vaul-drawer-direction=right]:sm:max-w-xl">
                    <aside className="flex flex-col h-full p-3 bg-white rounded-l-md">
                        <div className="h-[90vh]">
                            <div className="flex items-center justify-between">
                                <h3 className="my-0 text-lg font-semibold">Finance Request</h3>
                                <DrawerTrigger asChild className="">
                                    <X size={20} className="cursor-pointer" />
                                </DrawerTrigger>
                            </div>
                            {/* Document Preview */}
                            <div className="p-4">
                                {[sampleDocuments[fileKey]] ? (
                                    <img
                                        src={[sampleDocuments[fileKey]]}
                                        alt={sampleDocuments[fileKey]}
                                        className="w-full h-auto rounded-lg"
                                    />
                                ) : (
                                    <p className="text-center text-gray-500">
                                        No document available
                                    </p>
                                )}
                            </div>
                        </div>
                        <div>
                            <GenericBtn text="Use template" disabled={false} onClick={() => {}} />
                        </div>
                    </aside>
                </DrawerContent>
            </main>
        </Drawer>
    );
};

export { GenericBtn, validateFile };
export default FinanceRequest;
